import React, { useEffect } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { FormOptions } from '@data-driven-forms/react-form-renderer/dist/cjs/renderer-context';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import FormTemplateRenderProps from '@data-driven-forms/react-form-renderer/dist/cjs/form-template-render-props';
import { useForgotPassword } from '../api';
import { HcpFormRenderer, HcpFormTemplate, PublicLayout, ResourceContent } from '../components';
import { ResourceForgotPasswordAuthLayoutHeader } from '../components';
import { ForgotPasswordAuthLayout } from '../components/ForgotPasswordAuthLayout';
import {
  clientApplicationNameSelector,
  clientApplicationVersionSelector,
  getConfigurationDataKey,
  getForgotPasswordFormSchema,
  ROUTE_PATHS
} from '../utils';
import { AnyType, GeneralObject } from '../interfaces';
import { AppRootState, navigateToForgotPasswordSuccess } from '../redux';

export const ForgotPasswordView = () => {
  const dispatch = useDispatch();
  const result = useForgotPassword({ lazy: true });
  const isError = result.data && (result.data?.errors?.length ?? 0) > 0;
  const isComplete = result.response?.status === 200 && !isError;
  const applicationName = useSelector(clientApplicationNameSelector);
  const applicationVersion = useSelector(clientApplicationVersionSelector);
  const isLoginImage = useSelector(
    (state: AppRootState) => ((state.app?.entities?.resources as AnyType) ?? {})['login.image'] as AnyType
  );

  const onSubmit = async (values: GeneralObject) => {
    await result.refetch({
      queryParams: {
        userName: String(values.username),
        applicationName,
        applicationVersion
      }
    });
  };

  const renderTemplate = (formProps: FormTemplateRenderProps) => {
    return <ForgotPasswordFormTemplate {...formProps} isError={isError} />;
  };

  useEffect(() => {
    if (!isComplete) return;

    dispatch(navigateToForgotPasswordSuccess());
  }, [isComplete, dispatch]);

  return (
    <PublicLayout fluid className='authlayout_custom_image'>
      <ForgotPasswordAuthLayout>
        <ResourceForgotPasswordAuthLayoutHeader contentKey={getConfigurationDataKey('forgot-password.title')}>
          <ResourceContent contentKey={getConfigurationDataKey('forgot-password.body')} />
        </ResourceForgotPasswordAuthLayoutHeader>

        <HcpFormRenderer
          contentKey={getConfigurationDataKey('form.forgot-password')}
          FormTemplate={renderTemplate}
          noErrorMessage
          onSubmit={onSubmit}
          schemaMethod={getForgotPasswordFormSchema}
        />
      </ForgotPasswordAuthLayout>
    </PublicLayout>
  );
};

const ForgotPasswordFormTemplate = (props: FormTemplateRenderProps) => {
  const renderUserNotFound = () => {
    return (
      <Col>
        <p className='text-danger fs-3' style={{ maxWidth: '370px' }}>
          <ResourceContent contentKey='forgot-password.error' />
        </p>
      </Col>
    );
  };

  return (
    <HcpFormTemplate
      {...props}
      renderControls={(formProps: FormOptions) => {
        const formState = formProps.getState();
        const { submitting, hasValidationErrors } = formState;
        return (
          <Row>
            {props.isError && renderUserNotFound()}
            <Col xs={12} className='d-inline-flex align-items-center gap-2'>
              <Button
                type='submit'
                variant='secondary'
                className='px-3 py-2'
                disabled={submitting || hasValidationErrors}
                data-testid='cmx__login-button'>
                {submitting ? 'Submitting...' : 'Send Email'}
                <i className='fa-thin fa-paper-plane-top ms-2'></i>
              </Button>
            </Col>
            <Col xs={12} className='label-text-custom-50-new mt-3'>
              <p className='fs-2'>
                <ResourceContent contentKey='general.register-teaser' />
              </p>
              <Link to={ROUTE_PATHS.registration} className='text-secondary' data-testid='cmx__login__register'>
                Register now
              </Link>
            </Col>
          </Row>
        );
      }}
    />
  );
};
